/**
 * Bundle builder
 * --------------
 * Tracks which variant cards are selected and adds them all to the cart in a
 * single /cart/add.js request, as separate line items so that automatic
 * "buy X get Y" discounts apply exactly as they would for normal adds.
 *
 * The summary bar can be rendered more than once (top / bottom / side rail).
 * Every instance is updated from the same state.
 */
if (!customElements.get('bundle-builder')) {
  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.cards = Array.from(this.querySelectorAll('[data-bundle-item]'));
      this.bars = Array.from(this.querySelectorAll('[data-bundle-bar]'));
      this.floatingBars = this.bars.filter((bar) => bar.dataset.floating === 'true');
      this.selectAllInput = this.querySelector('[data-bundle-select-all]');
      this.selectAllLabel = this.querySelector('[data-bundle-select-all-label]');
      this.selectAllWrap = this.querySelector('[data-bundle-select-all-wrap]');
      this.toolsCount = this.querySelector('[data-bundle-tools-count]');
      this.selectableCards = this.cards.filter((card) => {
        const input = card.querySelector('[data-bundle-checkbox]');
        return input && !input.disabled;
      });
      this.errorEl = this.querySelector('[data-bundle-error]');
      this.successEl = this.querySelector('[data-bundle-success]');

      this.minQuantity = parseInt(this.dataset.minQuantity, 10) || 0;
      this.targetQuantity = parseInt(this.dataset.targetQuantity, 10) || 0;
      this.targetQuantity2 = parseInt(this.dataset.targetQuantity2, 10) || 0;
      this.tier1Label = this.dataset.tier1Label || '';
      this.tier2Label = this.dataset.tier2Label || '';
      // A second tier below the first would make the progress bar nonsense.
      if (this.targetQuantity2 <= this.targetQuantity) this.targetQuantity2 = 0;
      this.floatingVisibility = this.dataset.floatingVisibility || 'on_select';
      this.storageKey = `bundle-builder:${this.dataset.sectionId}`;
      this.refreshedSections = [];
      this.cartCount = 0;

      this.addEventListener('change', this.handleChange.bind(this));
      // Capture phase, so we win even if the theme has its own card click handler.
      this.addEventListener('click', this.handleClick.bind(this), true);

      this.watchViewport();
      this.restore();
      this.refresh();

      // Count what is already in the cart toward the discount thresholds.
      // Async, so the page is interactive before this resolves.
      this.syncCartProgress();
    }

    /* ------------------------------------------------------------------ state */

    /** @returns {{id:number, qty:number, price:number}[]} */
    getSelection() {
      return this.cards.reduce((acc, card) => {
        const input = card.querySelector('[data-bundle-checkbox]');
        if (!input || !input.checked) return acc;

        const qtyInput = card.querySelector('[data-bundle-qty-input]');
        const qty = qtyInput ? this.clampQty(qtyInput.value) : 1;

        acc.push({
          id: parseInt(input.dataset.variantId, 10),
          qty,
          price: parseInt(input.dataset.price, 10) || 0
        });
        return acc;
      }, []);
    }

    clampQty(value) {
      const n = parseInt(value, 10);
      if (Number.isNaN(n) || n < 1) return 1;
      return n;
    }

    /* ----------------------------------------------------------------- events */

    handleChange(evt) {
      const target = evt.target;

      if (target.matches('[data-bundle-select-all]')) {
        this.hideMessages();
        this.setAll(target.checked);
        return;
      }

      if (target.matches('[data-bundle-checkbox]')) {
        this.hideMessages();
        this.refresh();
        return;
      }

      if (target.matches('[data-bundle-qty-input]')) {
        target.value = this.clampQty(target.value);
        const input = target.closest('[data-bundle-item]').querySelector('[data-bundle-checkbox]');
        if (input && !input.disabled) input.checked = true;
        this.refresh();
      }
    }

    handleClick(evt) {
      /**
       * The theme stretches `.card-link::after` across the card's info container
       * so the whole area links to the product page. The CSS neutralises it
       * inside bundle cards, but if a theme update reinstates it — or another
       * script binds a click handler to the card — this stops a click on our
       * controls from navigating away.
       */
      // Let the product-page link behave like a normal link.
      if (evt.target.closest('.bundle-card__details')) return;

      const control = evt.target.closest('.bundle-card__overlay, [data-bundle-qty]');
      if (control) evt.stopPropagation();

      const step = evt.target.closest('[data-bundle-qty-up], [data-bundle-qty-down]');
      if (step) {
        evt.preventDefault();
        const wrap = step.closest('[data-bundle-qty]');
        const input = wrap.querySelector('[data-bundle-qty-input]');
        const delta = step.hasAttribute('data-bundle-qty-up') ? 1 : -1;
        input.value = this.clampQty(parseInt(input.value, 10) + delta);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      if (evt.target.closest('[data-bundle-submit]')) {
        evt.preventDefault();
        this.addToCart();
        return;
      }

      if (evt.target.closest('[data-bundle-clear]')) {
        evt.preventDefault();
        this.clearSelection();
      }
    }

    /* -------------------------------------------------------------- cart sync */

    /**
     * Tally how many of this bundle's variants are already in the cart.
     *
     * This does NOT tick any checkboxes. A checked card always means "add this",
     * so the selection stays a pure intention and add-to-cart stays a plain add.
     * The tally only feeds the progress bar, so someone who added three titles
     * earlier sees the discount threshold counted from three, not from zero.
     *
     * Only variants in this builder are counted. The rest of the cart is
     * irrelevant to a collection-scoped offer and would overstate progress.
     */
    async syncCartProgress() {
      const cart = await this.fetchCart();
      if (!cart || !Array.isArray(cart.items)) return;

      const bundleIds = new Set(this.cards.map((c) => c.dataset.variantId));
      let inCart = 0;

      cart.items.forEach((line) => {
        const id = String(line.variant_id || line.id);
        if (bundleIds.has(id)) inCart += line.quantity;
      });

      const changed = inCart !== this.cartCount;
      this.cartCount = inCart;
      if (changed) this.refresh();
    }

    /* ------------------------------------------------------------ persistence */

    /**
     * The selection is kept in sessionStorage so it survives leaving the page —
     * following a product link, hitting back, or reloading. It is deliberately
     * session rather than local storage: a bundle half-built three days ago is
     * not something a shopper expects to still be waiting for them.
     */
    save() {
      try {
        const state = this.getSelection().map((item) => [item.id, item.qty]);
        if (state.length) {
          sessionStorage.setItem(this.storageKey, JSON.stringify(state));
        } else {
          sessionStorage.removeItem(this.storageKey);
        }
      } catch (e) {
        /* Private mode or storage full — selection just will not persist. */
      }
    }

    restore() {
      let state;
      try {
        state = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
      } catch (e) {
        return;
      }
      if (!Array.isArray(state) || !state.length) return;

      state.forEach(([id, qty]) => {
        const card = this.cards.find((c) => c.dataset.variantId === String(id));
        if (!card) return; // Variant no longer in the bundle — skip it.

        const input = card.querySelector('[data-bundle-checkbox]');
        if (!input || input.disabled) return;
        input.checked = true;

        const qtyInput = card.querySelector('[data-bundle-qty-input]');
        if (qtyInput) qtyInput.value = this.clampQty(qty);
      });
    }

    forget() {
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (e) {
        /* no-op */
      }
    }

    /* ------------------------------------------------------------------- state */

    /** Tick or untick every selectable card. Sold out cards are left alone. */
    setAll(state) {
      this.selectableCards.forEach((card) => {
        const input = card.querySelector('[data-bundle-checkbox]');
        if (input) input.checked = state;
      });
      this.refresh();
    }

    /**
     * Keep the select-all switch in step with the cards. Three states: none,
     * some (knob parked halfway) and all.
     */
    updateSelectAll(selectedCards) {
      if (!this.selectAllInput) return;

      const total = this.selectableCards.length;
      const all = total > 0 && selectedCards === total;

      this.selectAllInput.checked = all;

      // A switch has no native indeterminate rendering, so drive it with a class.
      if (this.selectAllWrap) {
        this.selectAllWrap.classList.toggle('is-partial', selectedCards > 0 && !all);
      }

      if (this.selectAllLabel) {
        this.selectAllLabel.textContent = all
          ? this.dataset.textDeselectAll || 'Deselect all'
          : this.dataset.textSelectAll || 'Select all';
      }

      if (this.toolsCount) {
        this.toolsCount.textContent = `${selectedCards} of ${total} selected`;
      }
    }

    clearSelection() {
      this.cards.forEach((card) => {
        const input = card.querySelector('[data-bundle-checkbox]');
        if (input) input.checked = false;
      });
      this.hideMessages();
      this.refresh();
    }

    /* -------------------------------------------------------------------- ui */

    /** Run a callback against the matching element inside every bar instance. */
    eachBar(selector, fn) {
      this.bars.forEach((bar) => {
        bar.querySelectorAll(selector).forEach((el) => fn(el, bar));
      });
    }

    refresh() {
      const selection = this.getSelection();
      const count = selection.reduce((n, item) => n + item.qty, 0);
      const total = selection.reduce((n, item) => n + item.qty * item.price, 0);

      let selectedCards = 0;
      this.cards.forEach((card) => {
        const input = card.querySelector('[data-bundle-checkbox]');
        const on = Boolean(input && input.checked);
        card.classList.toggle('is-selected', on);
        if (on) selectedCards += 1;
      });

      this.updateSelectAll(selectedCards);

      this.eachBar('[data-bundle-count]', (el) => {
        el.textContent = count ? this.text('textSelected', { count }) : this.text('textEmpty', {});
      });

      this.eachBar('[data-bundle-total-wrap]', (el) => {
        el.hidden = count === 0;
      });
      this.eachBar('[data-bundle-total]', (el) => {
        el.textContent = this.formatMoney(total);
      });

      this.eachBar('[data-bundle-clear]', (el) => {
        el.hidden = count === 0;
      });

      const enabled = count > 0 && count >= this.minQuantity;
      this.eachBar('[data-bundle-submit]', (el) => {
        el.disabled = !enabled || this.loading;
      });

      if (!this.loading) {
        this.eachBar('[data-bundle-submit-text]', (el) => {
          el.textContent = count ? this.text('textButton', { count }) : this.text('textButtonEmpty', {});
        });
      }

      this.updateProgress(count);
      this.updateFloating(count);
      this.save();
    }

    /**
     * Progress across one or two tiers.
     *
     * `count` is what is selected right now; `cartCount` is how many of this
     * bundle's variants are already in the cart. The thresholds are measured
     * against the sum, because that is what the shopper will actually own once
     * they add — showing progress from zero when the cart already has three
     * would send them past the discount without telling them.
     *
     * The bar is scaled to the furthest tier, with a notch marking the first.
     */
    updateProgress(count) {
      if (!this.targetQuantity) return;

      const total = count + (this.cartCount || 0);
      const t1 = this.targetQuantity;
      const t2 = this.targetQuantity2;
      const ceiling = t2 || t1;

      const message = this.progressMessage(total, t1, t2);
      const reachedAny = total >= t1;
      const reachedAll = total >= ceiling;

      this.eachBar('[data-bundle-progress]', (el) => {
        el.hidden = false;
        el.innerHTML = '';
        el.appendChild(document.createTextNode(message));

        if (this.cartCount) {
          const note = document.createElement('span');
          note.className = 'bundle-builder__cart-note';
          note.textContent = this.text('textInCart', { cart: this.cartCount });
          el.appendChild(note);
        }

        el.classList.toggle('bundle-builder__progress--complete', reachedAll);
        el.classList.toggle('bundle-builder__progress--tier1', reachedAny && !reachedAll);
      });

      this.eachBar('[data-bundle-progress-fill]', (el) => {
        el.style.width = `${Math.min((total / ceiling) * 100, 100)}%`;
        el.classList.toggle('is-complete', reachedAll);
        el.classList.toggle('is-tier1', reachedAny && !reachedAll);
      });

      // The notch sits where the first tier falls along the scaled bar.
      this.eachBar('[data-bundle-progress-marker]', (el) => {
        if (!t2) {
          el.hidden = true;
          return;
        }
        el.hidden = false;
        el.style.insetInlineStart = `${(t1 / t2) * 100}%`;
        el.classList.toggle('is-reached', reachedAny);
      });
    }

    /** Pick the right line for where the shopper is across the tiers. */
    progressMessage(total, t1, t2) {
      if (total < t1) {
        return this.text('textProgress', {
          remaining: t1 - total,
          target: t1,
          count: total,
          tier: this.tier1Label
        });
      }

      if (t2 && total < t2) {
        return this.text('textTierNext', {
          remaining: t2 - total,
          target: t2,
          count: total,
          tier: this.tier1Label,
          next: this.tier2Label
        });
      }

      return this.text('textUnlocked', {
        count: total,
        target: t2 || t1,
        tier: t2 ? this.tier2Label : this.tier1Label
      });
    }

    /* ------------------------------------------------------- floating mobile bar */

    watchViewport() {
      if (!this.floatingBars.length) return;

      this.classList.add('has-floating-bar');

      if (this.floatingVisibility === 'in_view' && 'IntersectionObserver' in window) {
        this.inView = false;
        const observer = new IntersectionObserver(
          (entries) => {
            this.inView = entries[0].isIntersecting;
            this.refresh();
          },
          { rootMargin: '0px 0px -20% 0px' }
        );
        observer.observe(this);
      }
    }

    updateFloating(count) {
      if (!this.floatingBars.length) return;

      const visible = this.floatingVisibility === 'in_view' ? this.inView !== false : count > 0;

      this.floatingBars.forEach((bar) => {
        bar.classList.toggle('is-visible', visible);
      });

      // Reserve space so the last row of cards is never trapped under the bar.
      const height = this.floatingBars[0].offsetHeight;
      if (height) {
        this.style.setProperty('--bundle-floating-height', `${height + 16}px`);
      }
    }

    /* ----------------------------------------------------------------- helpers */

    text(key, tokens) {
      const raw = this.dataset[key] || '';
      return raw
        .replace(/\[count\]/g, tokens.count != null ? tokens.count : '')
        .replace(/\[remaining\]/g, tokens.remaining != null ? tokens.remaining : '')
        .replace(/\[target\]/g, tokens.target != null ? tokens.target : '')
        .replace(/\[tier\]/g, tokens.tier != null ? tokens.tier : '')
        .replace(/\[next\]/g, tokens.next != null ? tokens.next : '')
        .replace(/\[cart\]/g, tokens.cart != null ? tokens.cart : '');
    }

    formatMoney(cents) {
      const amount = cents / 100;
      try {
        const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency
        }).format(amount);
      } catch (e) {
        return `$${amount.toFixed(2)}`;
      }
    }

    setLoading(state) {
      this.loading = state;
      this.eachBar('[data-bundle-submit]', (el) => {
        el.disabled = state;
        el.classList.toggle('is-loading', state);
      });
      if (state) {
        this.eachBar('[data-bundle-submit-text]', (el) => {
          el.textContent = this.dataset.textAdding || 'Adding...';
        });
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.dataset.textError;
      this.errorEl.hidden = false;
      this.errorEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    showSuccess() {
      if (!this.successEl) return;
      this.successEl.textContent = this.dataset.textAdded || '';
      this.successEl.hidden = false;
    }

    hideMessages() {
      if (this.errorEl) this.errorEl.hidden = true;
      if (this.successEl) this.successEl.hidden = true;
    }

    /* ------------------------------------------------------------------ cart */

    async addToCart() {
      const selection = this.getSelection();
      if (!selection.length || this.loading) return;

      this.hideMessages();
      this.setLoading(true);

      const addUrl = `${this.dataset.cartAddUrl || '/cart/add'}.js`;

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: selection.map((item) => ({ id: item.id, quantity: item.qty }))
          })
        });

        const data = await response.json();

        if (!response.ok) {
          // /cart/add.js is atomic — if one line fails, nothing is added.
          this.setLoading(false);
          this.refresh();
          this.showError(data.description || data.message || this.dataset.textError);
          return;
        }

        const cart = await this.fetchCart();

        // Re-render the drawer markup first, then tell the theme, then open it.
        // Doing it in this order means the drawer is already correct if the
        // theme's own listeners do nothing.
        // Items just added now belong to the cart tally, not the selection.
        if (cart && Array.isArray(cart.items)) {
          const bundleIds = new Set(this.cards.map((c) => c.dataset.variantId));
          this.cartCount = cart.items.reduce(
            (n, line) => (bundleIds.has(String(line.variant_id || line.id)) ? n + line.quantity : n),
            0
          );
        }

        const countPatched = this.updateCartCount(cart);
        await this.refreshCartSections(countPatched);
        this.notifyTheme(cart, data.items || []);

        this.setLoading(false);
        this.forget();
        this.clearSelection();

        const after = this.dataset.afterAdd;
        if (after === 'cart') {
          window.location.href = this.dataset.cartUrl || '/cart';
          return;
        }

        if (after === 'drawer') {
          const opened = this.openCartDrawer();
          if (!opened) this.showSuccess();
          return;
        }

        this.showSuccess();
      } catch (error) {
        console.error('[bundle-builder]', error);
        this.setLoading(false);
        this.refresh();
        this.showError();
      }
    }

    /**
     * Ask Shopify to re-render the sections that display cart state and swap
     * their markup in.
     *
     * Dispatching an event only works if the theme happens to listen for the
     * name we guessed. This does not depend on that: the Section Rendering API
     * returns the same HTML the server would send on a fresh page load, so the
     * drawer and the header count end up correct either way.
     */
    async refreshCartSections(skipHeader) {
      this.refreshedSections = [];
      let targets = this.findCartSections();
      if (skipHeader) {
        targets = targets.filter((t) => !/header/i.test(t.id) || /cart/i.test(t.id));
      }
      if (!targets.length) return;

      const ids = targets.map((t) => t.id).join(',');
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      try {
        const res = await fetch(`${root}?sections=${encodeURIComponent(ids)}`);
        if (!res.ok) return;
        const sections = await res.json();

        targets.forEach(({ id, el }) => {
          const markup = sections[id];
          if (!markup) return;

          const doc = new DOMParser().parseFromString(markup, 'text/html');
          const fresh = doc.querySelector(`#shopify-section-${id}`) || doc.body;
          if (!fresh) return;

          // Replacing innerHTML keeps the wrapper (and its id) intact. Custom
          // elements inside are upgraded by the browser automatically.
          el.innerHTML = fresh.innerHTML;
          this.refreshedSections.push(id);
        });
      } catch (e) {
        console.warn('[bundle-builder] could not refresh cart sections', e);
      }
    }

    /**
     * Find the sections that render cart state.
     *
     * Element names differ between themes and the count bubble only exists when
     * the cart is non-empty, so specific selectors are unreliable. Shopify wraps
     * every section in `#shopify-section-<id>`, and the id carries the section's
     * filename — so matching on that finds the drawer and header whatever their
     * internal markup looks like. Section group ids such as
     * `sections--123__header` still contain the name, so they match too.
     */
    findCartSections() {
      const found = new Map();

      const add = (node) => {
        if (!node) return;
        const wrapper = node.closest ? node.closest('[id^="shopify-section-"]') : null;
        if (!wrapper) return;
        const id = wrapper.id.replace('shopify-section-', '');
        if (!found.has(id)) found.set(id, { id, el: wrapper });
      };

      // Known elements first, when they happen to be present.
      [
        'cart-drawer',
        '#cart-drawer',
        '[data-cart-drawer]',
        '.header__cart-count',
        '[data-cart-count]',
        '.js-cart-count'
      ].forEach((sel) => {
        try {
          add(document.querySelector(sel));
        } catch (e) {
          /* invalid selector for this theme — ignore */
        }
      });

      // Then anything whose section id mentions the cart or the header.
      document.querySelectorAll('[id^="shopify-section-"]').forEach((wrapper) => {
        const id = wrapper.id.replace('shopify-section-', '');
        if (/cart|header/i.test(id) && !found.has(id)) {
          found.set(id, { id, el: wrapper });
        }
      });

      return Array.from(found.values());
    }

    /**
     * Update the header count in place from the cart JSON. Cheaper and safer
     * than re-rendering the header, which would tear down menus and any sticky
     * header behaviour. Returns false when there is no bubble to update — which
     * is what happens when the cart was empty, since the theme omits the markup
     * entirely. In that case the caller re-renders the header instead.
     */
    updateCartCount(cart) {
      if (!cart || typeof cart.item_count !== 'number') return false;

      const bubble = document.querySelector('.header__cart-count, [data-cart-count], .js-cart-count');
      if (!bubble) return false;

      const visible = bubble.querySelector('[aria-hidden="true"]') || bubble;
      visible.textContent = cart.item_count < 10000 ? cart.item_count : '';
      bubble.hidden = cart.item_count === 0;
      return true;
    }

    async fetchCart() {
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${root}cart.js`);
        return await res.json();
      } catch (e) {
        return null;
      }
    }

    /**
     * Tell the theme the cart changed. The event name is configurable in the
     * section settings so this keeps working if the theme is updated.
     */
    notifyTheme(cart, items) {
      const detail = { cart, items };
      const names = [this.dataset.cartEvent, 'on:cart:add', 'on:cart:change', 'cart:refresh'];

      [...new Set(names.filter(Boolean))].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      });
    }

    openCartDrawer() {
      const drawer =
        document.querySelector('cart-drawer') ||
        document.getElementById('cart-drawer') ||
        document.querySelector('[data-cart-drawer]') ||
        document.querySelector('.cc-cart-drawer [id*="cart"], .cc-cart-drawer .drawer');

      if (!drawer) {
        console.warn(
          '[bundle-builder] no cart drawer element found. Sections refreshed:',
          this.refreshedSections
        );
        return false;
      }

      if (typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
      if (typeof drawer.show === 'function') {
        drawer.show();
        return true;
      }
      if (typeof drawer.showModal === 'function') {
        drawer.showModal();
        return true;
      }

      return false;
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}